import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { cn } from '@/lib/utils.ts';
import { TemplateVariableDefinitionDto } from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';
import { TemplateVariable } from './extensions/TemplateVariable.ts';
import { TemplateVariableBlock } from './extensions/TemplateVariableBlock.ts';
import { TemplateImage } from './image/TemplateImage.ts';
import { BlockFontSize } from './extensions/BlockFontSize.ts';
import {
  StyledTable,
  StyledTableCell,
  StyledTableHeader,
  TableRow,
} from './extensions/tableExtensions.ts';
import { VariableSuggestion } from './extensions/VariableSuggestion.ts';
import { VariablesProvider } from './contexts/VariablesContext.tsx';
import { convertTemplateToTiptap } from './converters/templateToTiptap.ts';
import { convertTiptapToTemplate } from './converters/tiptapToTemplate.ts';
import { TemplateImageSupport } from './image/templateImageSupport.ts';
import RichTextToolbar from './RichTextToolbar.tsx';
import VariableInsertPanel from './components/VariableInsertPanel.tsx';
import SuggestionPopup from './components/SuggestionPopup.tsx';
import TableBubbleMenu from './components/TableBubbleMenu.tsx';
import ImageBubbleMenu from './image/ImageBubbleMenu.tsx';
import { CD_DOC_CLASS } from '@/lib/templateStyles/documentBaseStyles.ts';
import { useDocumentBaseStyles } from '@/lib/templateStyles/useDocumentBaseStyles.ts';
import { RichTextEditorSkeleton } from './RichTextEditorSkeleton.tsx';
import './richTextEditor.css';

export interface RichTextEditorProps {
  content: string;
  variables?: TemplateVariableDefinitionDto[];
  onChange?: (html: string) => void;
  disabled?: boolean;
  className?: string;
  error?: string;
  htmlMode?: boolean;
  imageSupport?: TemplateImageSupport;
  imagesLoading?: boolean;
}

const RichTextEditor = (props: RichTextEditorProps) => {
  if (!props.htmlMode && props.variables === undefined) {
    return <RichTextEditorSkeleton className={props.className} />;
  }
  // When image support is wired, wait for the media library before mounting:
  // the initial content parse resolves `{{image}}` references to preview URLs
  // and `useEditor` does not reparse afterwards (same gate as for variables).
  if (props.imagesLoading) {
    return <RichTextEditorSkeleton className={props.className} />;
  }
  return <RichTextEditorInner {...props} />;
};

const RichTextEditorInner = ({
  content,
  variables,
  onChange,
  disabled = false,
  className,
  error,
  htmlMode = false,
  imageSupport,
}: RichTextEditorProps) => {
  const variablesRef = useRef(variables ?? []);
  variablesRef.current = variables ?? [];

  // Pull the canonical `.cd-doc` base CSS (served by the backend, the same bytes
  // it injects into PDF/mail) so the editor renders 1:1 with the document. Loaded
  // here, at the rich-text surface, rather than app-wide.
  useDocumentBaseStyles();

  const imagesEnabled = !htmlMode && !!imageSupport;

  const tiptapContent = useMemo(
    () =>
      htmlMode
        ? content
        : convertTemplateToTiptap(
            content,
            variables,
            imageSupport?.resolveImageUrl,
          ),
    [content, htmlMode, variables, imageSupport],
  );

  const extensions = useMemo(
    () => [
      // Headings are intentionally disabled: with block-level font sizes a
      // "heading" is just a large paragraph, so the size picker covers it.
      StarterKit.configure({ heading: false }),
      Link.configure({ openOnClick: false }),
      Underline,
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      TextStyle,
      Color,
      FontSize,
      BlockFontSize,
      Highlight.configure({ multicolor: true }),
      StyledTable,
      TableRow,
      StyledTableHeader,
      StyledTableCell,
      ...(htmlMode
        ? []
        : [
            TemplateVariable,
            TemplateVariableBlock,
            VariableSuggestion.configure({
              getVariables: () => variablesRef.current,
            }),
          ]),
      ...(imagesEnabled ? [TemplateImage] : []),
    ],
    [htmlMode, imagesEnabled],
  );

  const editor = useEditor({
    extensions,
    content: tiptapContent,
    editable: !disabled,
  });

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      if (!onChange) return;
      onChange(
        htmlMode ? editor.getHTML() : convertTiptapToTemplate(editor.getHTML()),
      );
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, onChange, htmlMode]);

  if (!editor) return null;

  return (
    <VariablesProvider variables={variables ?? []}>
      <div
        className={cn(
          'rich-text-editor overflow-hidden rounded-md border bg-basic-white',
          error ? 'border-error-100' : 'border-neutral-200',
          className,
        )}
      >
        {variables && variables.length > 0 && (
          <VariableInsertPanel editor={editor} variables={variables} />
        )}
        <RichTextToolbar editor={editor} imageSupport={imageSupport} />
        <EditorContent
          editor={editor}
          className={cn(
            CD_DOC_CLASS,
            'px-4 py-3 [&_.ProseMirror]:min-h-[300px] [&_.ProseMirror]:outline-none',
          )}
        />
        <TableBubbleMenu editor={editor} />
        {imagesEnabled && <ImageBubbleMenu editor={editor} />}
      </div>
      <SuggestionPopup />
    </VariablesProvider>
  );
};

export default RichTextEditor;
